import { useCallback, useEffect, useRef } from 'react';

import notifySound from 'assets/sounds/notify.mp3';

import { shouldBeep, toHHMM } from 'core/utils/notificationAlarm';

import { ISoundAlarm } from './useNotificationBadge';

/**
 * Как часто ПРОВЕРЯЕМ, не пора ли звенеть. К интервалу повтора отношения не
 * имеет: решение принимается сравнением стенных часов, а этот тик — просто
 * возможность его принять.
 */
const TICK_MS = 15 * 1000;

/**
 * Метка последнего сигнала — общая на все вкладки ERP.
 *
 * Без неё три открытые вкладки дали бы три сигнала одновременно, и человек
 * выключил бы звук в тот же день.
 */
const LAST_BEEP_KEY = 'erp:notify:lastBeepAt';

const readLastBeep = (): number => {
  try {
    return Number(window.localStorage.getItem(LAST_BEEP_KEY)) || 0;
  } catch {
    // Приватный режим или запрет на хранилище — звук от этого работать не
    // перестаёт, просто вкладки перестают договариваться между собой.
    return 0;
  }
};

const writeLastBeep = (at: number): void => {
  try {
    window.localStorage.setItem(LAST_BEEP_KEY, String(at));
  } catch {
    /* см. выше */
  }
};

export interface IUseNotificationSoundArgs {
  alarm: ISoundAlarm;
  soundEnabled: boolean;
  repeatMinutes: number;
  quietFrom?: string | null;
  quietTo?: string | null;
  panelOpen: boolean;
  mutedUntil: number;
  unlocked: boolean;
  onUnlocked: () => void;
  /** Освежить счётчик перед решением — см. комментарий ниже. */
  refetchBadge: () => Promise<unknown>;
}

export interface IUseNotificationSound {
  /** Разблокировать звук и проиграть образец — только из обработчика клика. */
  unlockAndPreview: () => Promise<boolean>;
}

/**
 * Звуковое напоминание о срочной задаче (ТЗ 0068).
 *
 * Три вещи, которые здесь неочевидны и потому подписаны:
 *
 * 1. **Браузер не даст проиграть звук без жеста пользователя.** Поэтому
 *    включение галочки в настройках само разблокирует аудио и проигрывает
 *    образец — это одновременно и разрешение, и честная демонстрация «вот так
 *    это звучит». После перезагрузки разрешение сбрасывается, и мы ловим
 *    первый же клик по документу. Пока не разблокировано, играть НЕ пытаемся:
 *    попытка даёт `NotAllowedError` в консоли и ни одного звука.
 *
 * 2. **Интервал считается по стенным часам.** В фоновой вкладке таймеры
 *    троттлятся, а `requestAnimationFrame` не вызывается вовсе — счёт тиками
 *    давал бы то опоздание вдвое, то пачку сигналов при возврате.
 *
 * 3. **Перед сигналом счётчик перезапрашивается.** `usePageVisiblePoll`
 *    ОСТАНАВЛИВАЕТ опрос в скрытой вкладке — значит в фоне число устаревает, и
 *    напоминатель звенел бы о задаче, которую уже закрыли с другого
 *    устройства. Один запрос раз в интервал повтора стоит копейки.
 */
export const useNotificationSound = (
  args: IUseNotificationSoundArgs,
): IUseNotificationSound => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Аргументы в ref, чтобы тикающий эффект не пересоздавался на каждый рендер
  // (иначе интервал сбрасывался бы и сигнал не наступал никогда).
  const argsRef = useRef(args);
  argsRef.current = args;

  const audio = useCallback((): HTMLAudioElement => {
    if (!audioRef.current) {
      audioRef.current = new Audio(notifySound);
      audioRef.current.preload = 'auto';
    }
    return audioRef.current;
  }, []);

  const unlock = useCallback(async (): Promise<boolean> => {
    const element = audio();
    const { volume } = element;
    element.volume = 0;
    try {
      await element.play();
      element.pause();
      element.currentTime = 0;
      argsRef.current.onUnlocked();
      return true;
    } catch {
      return false;
    } finally {
      element.volume = volume;
    }
  }, [audio]);

  const unlockAndPreview = useCallback(async () => {
    const ok = await unlock();
    if (!ok) return false;
    const element = audio();
    element.currentTime = 0;
    await element.play().catch(() => undefined);
    return true;
  }, [audio, unlock]);

  // Первый клик по документу разблокирует звук — но только когда он включён и
  // ещё не разблокирован, иначе мы вешали бы слушатель всем и всегда.
  useEffect(() => {
    if (!args.soundEnabled || args.unlocked) return undefined;
    const handler = () => {
      unlock();
    };
    document.addEventListener('pointerdown', handler, { once: true });
    document.addEventListener('keydown', handler, { once: true });
    return () => {
      document.removeEventListener('pointerdown', handler);
      document.removeEventListener('keydown', handler);
    };
  }, [args.soundEnabled, args.unlocked, unlock]);

  useEffect(() => {
    const tick = async () => {
      const { current } = argsRef;
      if (!current.soundEnabled || !current.unlocked) return;

      const lastBeepAt = readLastBeep();
      const repeatMs = Math.max(1, current.repeatMinutes) * 60 * 1000;
      const now = Date.now();
      // Не пора — не ходим в сеть. Проверка дешёвая и отсекает 99% тиков.
      if (lastBeepAt && now - lastBeepAt < repeatMs) return;

      // Освежаем счётчик: в скрытой вкладке опрос стоит, и `alarm` мог
      // устареть.
      await current.refetchBadge().catch(() => undefined);
      const fresh = argsRef.current;

      if (
        !shouldBeep({
          now: Date.now(),
          alarmActive: fresh.alarm.active,
          soundEnabled: fresh.soundEnabled,
          unlocked: fresh.unlocked,
          panelOpen: fresh.panelOpen,
          mutedUntil: fresh.mutedUntil,
          lastBeepAt: readLastBeep(),
          repeatMs,
          nowHHMM: toHHMM(new Date()),
          quietFrom: fresh.quietFrom,
          quietTo: fresh.quietTo,
        })
      ) {
        return;
      }

      writeLastBeep(Date.now());
      const element = audio();
      element.currentTime = 0;
      element.play().catch(() => undefined);
    };

    const timer = window.setInterval(tick, TICK_MS);
    // Возврат на вкладку и фокус окна — самые вероятные моменты, когда тик
    // «пропущен» дросселированием, поэтому проверяем и по ним.
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('focus', tick);
    };
  }, [audio]);

  // Тревога погасла (задачу закрыли) — начинаем следующий пожар с чистого
  // листа, а не «через сколько-то минут после прошлого сигнала».
  useEffect(() => {
    if (!args.alarm.active) writeLastBeep(0);
  }, [args.alarm.active]);

  return { unlockAndPreview };
};
